import './index.css'
import { updateChannelShareForm } from '@disco/state-management/reducers/forms/channel-share'
import { getTerritories } from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import { getUser } from '@disco/state-management/selectors/user'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useParams } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { User } from 'types/user'
import { updatePlaylistShareForm } from '@disco/state-management/actions/FormActions'
import { Button, Text } from 'components/ds'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import type { RootState } from 'store/configureStore'

const mapStateToProps = (
  state: RootState,
  {
    params: { shortlistId, playlistId, channelId },
  }: {
    params: { shortlistId?: string; playlistId?: string; channelId?: string }
  },
) => {
  let playlist = null

  if (shortlistId || playlistId) {
    playlist = playlistId
      ? getPlaylist(state, {
          id: parseInt(playlistId),
        })
      : getShortlistById(state, {
          id: shortlistId,
        })?.playlist
  }

  return {
    playlist,
    user: getUser(state),
    territories: getTerritories(state),
  }
}

class ShareWarningForm extends React.Component<
  {
    params: {
      playlistId?: string
      shortlistId?: string
      channelId?: string
    }
    playlist: Playlist | null | undefined
    user: User
    territories: Group[]
    updatePlaylistShareForm: typeof updatePlaylistShareForm
    updateChannelShareForm: typeof updateChannelShareForm
    getValidationMessages: (...args: Array<any>) => any
    validate: (...args: Array<any>) => any
    onSubmit: (territories: any) => void
    onDismiss?: (...args: Array<any>) => any
  },
  {
    selectedTerritories: Record<number, boolean>
  }
> {
  state = {
    selectedTerritories: {},
  }
  validatorTypes = strategy.createSchema({
    selectedTerritories: 'required|array|min:1',
  })
  _getSelectedTerritories = () =>
    Object.keys(this.state.selectedTerritories)
      .filter(key => this.state.selectedTerritories[parseInt(key, 10)])
      .map(key => parseInt(key, 10))
  getValidatorData = () => ({
    selectedTerritories: this._getSelectedTerritories(),
  })
  _onCheckboxSwitch = (id: number) =>
    this.setState({
      selectedTerritories: {
        ...this.state.selectedTerritories,
        [id]: !this.state.selectedTerritories[id],
      },
    })
  _onSubmit = () => {
    this.props.validate(error => {
      if (error && this.props.user.territories.length > 0) return

      const territories = this._getSelectedTerritories()

      const { channelId } = this.props.params
      if (channelId)
        this.props.updateChannelShareForm({
          territories,
        })
      else
        this.props.updatePlaylistShareForm({
          territories,
        })
      this.props.onSubmit(territories)
    })
  }
  _renderHelpText = (message: string) => <Text color="red">{message}</Text>

  render() {
    const territoriesRequired = this.props.getValidationMessages(
      'selectedTerritories',
    ).length
      ? 'You need to select at least one territory.'
      : ''

    const selectedTerritories = this._getSelectedTerritories()

    const hasTerritories = this.props.user.territories.length > 0
    return (
      <>
        {this.props.playlist &&
        this.props.playlist.has_any_territoryless_track ? (
          <>
            Some tracks in this playlist haven&#39;t had territories added.
            Please be careful when sharing.
          </>
        ) : null}
        {hasTerritories && (
          <>
            <Text>What territories would you like to share with?</Text>
            <div className="territory-checkboxes">
              {this.props.territories.map(t => (
                <Checkbox
                  key={t.id}
                  label={t.name}
                  checked={!!this.state.selectedTerritories[t.id]}
                  onChange={() => this._onCheckboxSwitch(t.id)}
                />
              ))}
            </div>
            {selectedTerritories.length > 1 ? (
              <div className="territory-warning">
                You’re selecting multiple territories to share to. Please be
                careful when sharing tracks to multiple territories.
              </div>
            ) : (
              <div className="territory-warning">
                {selectedTerritories.length === 0 &&
                  this._renderHelpText(territoriesRequired)}
              </div>
            )}
          </>
        )}
        <ButtonContainer alignRight>
          {this.props.onDismiss && (
            <Button color="gray" variant="soft" onClick={this.props.onDismiss}>
              Cancel
            </Button>
          )}
          <Button color="red" variant="soft" onClick={this._onSubmit}>
            Confirm
          </Button>
        </ButtonContainer>
      </>
    )
  }
}

const validatingComponent = validation(strategy)(ShareWarningForm)

const mapDispatchToProps = {
  updatePlaylistShareForm,
  updateChannelShareForm,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const ConnectedValidatingComponent = connector(validatingComponent)

function ShareWarningFormWithRouter(props: any) {
  const params =
    useParams<{
      playlistId?: string
      shortlistId?: string
      channelId?: string
    }>()
  return <ConnectedValidatingComponent {...props} params={params} />
}

export default ShareWarningFormWithRouter
